import { TreeSelectProps } from 'antd';
import { isObjectLike } from 'lodash';

import { initOfferIdentitiesSuccessAction } from 'containers/Offers/Offer/store';

import { mapTreeSelectIds, replaceItemInArray } from 'utils';

import { OfferItemIdentity, OfferItemIdentityUpdate } from 'codegen';
import {
  IDataResponse,
  InitialTableActions,
  TColumn,
  TEditableColumn,
  TFieldProps,
  TRequestCallback,
} from 'types';

import { getFormattedIdentities } from '../offerItemsHelpers';

export const handlePatchIdentity = (
  onPatchTableData: TRequestCallback<OfferItemIdentityUpdate>,
  offerId?: string,
  selectedItemId?: string | number,
) => ({
  id,
  merchants,
  ...rest
}: OfferItemIdentity) => {
  const retailers = merchants?.find(isObjectLike)
    ? merchants?.map((merchant) => ({ id: mapTreeSelectIds(merchant.id) }))
    : merchants?.map((ID) => ({ id: mapTreeSelectIds(ID as unknown as number) }));

  const payload = {
    ...rest,
    merchants: retailers,
  };

  onPatchTableData({
    payload,
    callbackURL: `/offers/details/${offerId}/items/${selectedItemId}/identities/${id}`,
  });
};

export const handleSuccessPatchIdentities = (
  identities: OfferItemIdentity[],
) => (
  { data }: IDataResponse<OfferItemIdentity>,
) => {
  const formattedIdentity = getFormattedIdentities(data);
  const ids = replaceItemInArray(identities, formattedIdentity);

  initOfferIdentitiesSuccessAction(ids);
};

export const handlePostIdentity = (
  onPostTableData: TRequestCallback<OfferItemIdentityUpdate>,
  offerId?: string,
  selectedItemId?: string | number,
) => ({
  merchants,
  ...rest
}: OfferItemIdentity) => {
  const payload = {
    ...rest,
    isActive: true,
    merchants: merchants?.map((id) => ({ id: mapTreeSelectIds(id as unknown as string) })),
  };

  onPostTableData({
    payload,
    callbackURL: `/offers/details/${offerId}/items/${selectedItemId}/identities`,
  });
};

export const handleSuccessPostIdentities = (
  identities: OfferItemIdentity[],
) => (
  { data }: IDataResponse<OfferItemIdentity>,
) => {
  const formattedIdentity = getFormattedIdentities(data);
  const ids = [...identities, formattedIdentity];

  initOfferIdentitiesSuccessAction(ids);
};

export const columns = (retailers?: TreeSelectProps['treeData']): TEditableColumn<OfferItemIdentity>[] => [{
  width: 80,
  title: 'Type',
  dataIndex: 'type',
  field: {
    type: TFieldProps.type.Select,
    options: Object.values(OfferItemIdentity.type).map((type) => ({ label: type, value: type })),
    allowClear: false,
    rules: [{
      message: 'Required',
      required: true,
    }],
  },
}, {
  width: 250,
  title: 'Value',
  dataIndex: 'value',
  field: {
    type: TFieldProps.type.Text,
    rules: [{
      message: 'Required',
      required: true,
    }],
  },
}, {
  type: TColumn.type.Tags,
  width: 350,
  title: 'Retailers',
  dataIndex: 'merchants',
  field: {
    type: TFieldProps.type.TreeSelect,
    treeData: retailers,
    placeholder: 'Select Retailer(s)',
    rules: [{
      message: 'Required',
      required: true,
    }],
  },
}, {
  type: TColumn.type.Percent,
  width: 80,
  dataIndex: 'partialMatchThreshold',
  titleInfo: {
    title: 'Partial Match Threshold',
    info: 'Reducing predefined Partial Match Threshold can result in an increased number of matches that require manual correction',
  },
  field: {
    type: TFieldProps.type.Percent,
    dependencies: ['fullMatchThreshold'],
    rules: [
      {
        message: 'Required',
        required: true,
      },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value || getFieldValue('fullMatchThreshold') > value) {
            return Promise.resolve();
          }

          return Promise.reject(new Error('Should be less than Full Match'));
        },
      }),
    ],
  },
}, {
  type: TColumn.type.Percent,
  width: 80,
  dataIndex: 'fullMatchThreshold',
  titleInfo: {
    title: 'Full Match Threshold',
    info: 'Reducing predefined Full Match Threshold can result in increased amount of matches that are falsely granted  with miles',
  },
  field: {
    type: TFieldProps.type.Percent,
    dependencies: ['partialMatchThreshold'],
    rules: [
      {
        message: 'Required',
        required: true,
      },
      ({ getFieldValue }) => ({
        validator(_, value) {
          if (!value || getFieldValue('partialMatchThreshold') < value) {
            return Promise.resolve();
          }

          return Promise.reject(new Error('Should be more than Partial Match'));
        },
      }),
    ],
  },
}];

export const initialTableActions = [{
  type: InitialTableActions.Edit,
}, {
  type: InitialTableActions.Toggle,
  title: 'offer identity',
}];
